import { useEffect, useState, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import {
  FORCE_SHOPPING_PREFS_MODAL_PATHNAME,
  WOMEN_UNIVERSE,
  LOCAL_KEY,
  SESSION_KEY,
} from '@constants/user-shopping-preferences';
import { useMutation, useQuery, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useBucket } from '@context/bucket.context';
import { isBot } from '@helpers/is-bot';
import useUser from '@hooks/user/use-user';
import { ShoppingPrefsPayload, UserShoppingPreferencesService } from '@services/user-shopping-preferences-service';
import { AppCookies } from '@enums/cookies';
import { isHomePage } from '@helpers/routing';
import Cookies from 'universal-cookie';
import { cookieOptions } from '@components/bucketTest/bucket.utils';
import { usePreferences } from '@context/preferences.context';
import logger from '@helpers/utils/logger/client';

type StorageSource = 'session' | 'local' | 'backend' | null;
type StorageToClear = 'session' | 'local' | 'all';

interface ShoppingPreferences extends ShoppingPrefsPayload {
  last_updated: string;
}

interface CookiePrefs extends ShoppingPrefsPayload {
  persistedSource: boolean;
}

type PreferencesWithoutTimestamp = Omit<ShoppingPreferences, 'last_updated'>;

interface SafeStorage {
  getLocal: () => ShoppingPreferences | null;
  setLocal: (prefs: ShoppingPreferences) => void;
  clearLocal: () => void;
  getSession: () => ShoppingPreferences | null;
  setSession: (prefs: ShoppingPreferences) => void;
  clearSession: () => void;
}

export interface UseShoppingPreferencesReturn {
  preferences: ShoppingPreferences | null;
  persistedPreferences: ShoppingPreferences | null;
  setPreferences: (newPrefs: PreferencesWithoutTimestamp) => Promise<void>;
  setSessionPreferences: (newPrefs: PreferencesWithoutTimestamp) => void;
  clearPreferences: (storage: StorageToClear) => void;
  source: StorageSource;
  isLoading: boolean;
  isError: boolean;
  shoppingPrefEnable: boolean;
  setShowPrefsModal: (show: boolean) => void;
  showPrefsModal: boolean;
  hasUserSelectedPrefs: boolean;
}

const nowISO = (): string => new Date().toISOString();

const logMessage = (message: string, level: 'info' | 'warn' | 'error', pathname?: string) => {
  let logMessage = message;
  if (pathname === '/') {
    logMessage = `[${pathname}] ${message}`;
    if (level === 'info') {
      logger.info(logMessage);
    } else if (level === 'warn') {
      logger.warn(logMessage);
    } else if (level === 'error') {
      logger.error(logMessage);
    }
  }
};

const createDefaultPreferences = (): ShoppingPreferences => ({
  universe: WOMEN_UNIVERSE,
  categories: undefined,
  custom: undefined,
  last_updated: nowISO(),
});

const safeStorage: SafeStorage = {
  getLocal: (): ShoppingPreferences | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  setLocal: (prefs: ShoppingPreferences) => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(LOCAL_KEY, JSON.stringify(prefs));
  },
  clearLocal: () => {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem(LOCAL_KEY);
  },
  getSession: (): ShoppingPreferences | null => {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  setSession: (prefs: ShoppingPreferences) => {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(prefs));
  },
  clearSession: () => {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.removeItem(SESSION_KEY);
  },
};

export const useShoppingPreferences = (): UseShoppingPreferencesReturn => {
  const { isAuthenticated } = useUser();
  const [prefs, setPrefs] = useState<ShoppingPreferences | null>(null);
  const [persistedPreferences, setPersistedPreferences] = useState<ShoppingPreferences | null>(null);
  const [source, setSource] = useState<StorageSource>(null);
  const [hasHydrated, setHasHydrated] = useState<boolean>(false);
  const [hasWindowLoaded, setHasWindowLoaded] = useState<boolean>(false);
  const [showPrefsModal, setShowPrefsModal] = useState<boolean>(false);
  const [hasUserSelectedPrefs, setHasUserSelectedPrefs] = useState<boolean>(false);
  const router = useRouter();
  const cookies = new Cookies();
  /* Using router.asPath because it must be a Client Component when using `usePathname()` from next/navigation.
     It works in dev/Vercel (lenient hydration), but Cloudflare’s strict server (Staging).
     The Sandbox crashes if client hooks run in a Server Component. */
  const pathname = router?.asPath;
  const query = router?.query;
  const isConditionalHomepage = (newUniverse): boolean => {
    // If the home page URL is slash only or have a universe and this universe is not already set - to prevent double loading
    return (
      isHomePage(router.pathname, pathname) ||
      ((query?.universe === '1' || query?.universe === '2') && query?.universe !== newUniverse.toString())
    );
  };

  const updateHomepageWithNewPrefs = (newPrefs) => {
    if (
      isConditionalHomepage(newPrefs?.universe) ||
      (pathname === FORCE_SHOPPING_PREFS_MODAL_PATHNAME && newPrefs?.universe !== prefs?.universe)
    ) {
      window?.location?.assign(`/?universe=${newPrefs?.universe}`);
    }
  };

  const { isFeatureEnabled } = useBucket();
  const shoppingPrefEnable: boolean = isFeatureEnabled((f) => f?.shoppingPreferencesEnableForWeb, true);

  const setGlobalPrefrences = (data: ShoppingPreferences, source: StorageSource, hasSelected: boolean = true) => {
    setPrefs(data);
    setSource(source);
    setHasUserSelectedPrefs(hasSelected);
  };

  const synchShoppingPrefs = (newPrefs: ShoppingPreferences, persistedSource: boolean) => {
    safeStorage.setSession(newPrefs);
    if (persistedSource) {
      safeStorage.setLocal(newPrefs);
      setPersistedPreferences(newPrefs);
    }
  };

  const setCookieShoppingPrefs = (newPrefs: ShoppingPrefsPayload, persistedSource: boolean) => {
    const cookieValue: CookiePrefs = {
      ...newPrefs,
      persistedSource: persistedSource,
    };
    cookies.set(AppCookies.USER_SHOPPING_PREFERENCES, JSON.stringify(cookieValue), cookieOptions);
  };
  // Wait for window load
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (document.readyState === 'complete') {
      setHasWindowLoaded(true);
    } else {
      const onLoad = (): void => setHasWindowLoaded(true);
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  // Hydrate from session/local
  useEffect(() => {
    const sessionPrefs = safeStorage.getSession();
    const localPrefs = safeStorage.getLocal();
    const cookiePrefs = cookies.get(AppCookies.USER_SHOPPING_PREFERENCES);
    if (sessionPrefs) {
      setGlobalPrefrences(sessionPrefs, 'session');
    } else if (localPrefs) {
      setGlobalPrefrences(localPrefs, 'local');
    } else if (cookiePrefs) {
      const currentPrefs = cookiePrefs;
      delete currentPrefs.source;
      setGlobalPrefrences(
        currentPrefs,
        cookiePrefs.persistedSource ? 'local' : 'session',
        cookiePrefs?.persistedSource,
      );
      synchShoppingPrefs(currentPrefs, cookiePrefs?.persistedSource);
    } else {
      // Set default preferences but don't mark as user-selected
      const defaultPrefs = createDefaultPreferences();
      setGlobalPrefrences(defaultPrefs, 'local', false);
    }
    setHasHydrated(true);
  }, [isAuthenticated]);

  // Backend update mutation
  const updatePrefsMutation: UseMutationResult<void, Error, ShoppingPreferences> = useMutation({
    mutationFn: async (prefs: ShoppingPreferences): Promise<void> => {
      await UserShoppingPreferencesService.updateShoppingPrefs({
        universe: prefs.universe,
        categories: prefs.categories,
        custom: prefs.custom,
      });
    },
    retry: 2,
  });

  // Query backend
  const prefsQuery: UseQueryResult<ShoppingPreferences, Error> = useQuery({
    queryKey: ['userPrefs'],
    queryFn: async (): Promise<ShoppingPreferences> => {
      const data = await UserShoppingPreferencesService.getShoppingPrefs();
      return {
        universe: data?.universe,
        categories: data?.categories,
        custom: data?.custom,
        last_updated: data?.updated_at,
      } as ShoppingPreferences;
    },
    enabled: isAuthenticated,
    onSuccess: (backendPrefs: ShoppingPreferences) => {
      const local: ShoppingPreferences | null = safeStorage.getLocal();

      // TODO: maybe in the futre, we need to check the other keys (categories and custom)
      if (shoppingPrefEnable && !backendPrefs?.universe) {
        if (local?.universe) {
          // When there are no backend prefs (newly signed-up users) but there are local prefs
          updatePrefsMutation.mutate(local);
        } else if (!prefs?.universe) {
          // When there are no backend prefs (newly signed-up users),
          // no local prefs (newly signed-up users (from different page than Homepage) or cleared local storage)
          // and no session prefs:
          // then, always show the modal on the homepage to set the prefs
          setShowPrefsModal(true);
        }
        return;
      }

      if (shoppingPrefEnable) {
        // Set the preferences with backend prefs
        safeStorage.setLocal(backendPrefs);
        setGlobalPrefrences(backendPrefs, 'backend');

        // If the session is not set, set the cookie to show the HP based on the user prefs
        if (!safeStorage.getSession()) {
          safeStorage.setSession(backendPrefs);
          setCookieShoppingPrefs(backendPrefs, true);
          updateHomepageWithNewPrefs(backendPrefs);
        }
      }
    },
    onError: (error) => {
      logMessage(
        `[useShoppingPreferences.hook] Error fetching user shopping preferences: ${error.message}`,
        'error',
        pathname,
      );
    },
  });

  const { language } = usePreferences();
  // The CMP popin is showing on every language but US.
  const initialCMPSettings = ['us'].includes(language);
  // Has Seen CMP should allow to trigger the Shopping Prefrences modal.
  const [hasSeenCMP, setHasSeenCMP] = useState<boolean>(initialCMPSettings);
  const cact = hasWindowLoaded ? window?.cact : undefined;

  // Memoize bot detection to avoid repeated calls
  const isUserBot = useMemo(() => {
    return typeof window !== 'undefined' ? isBot(window?.navigator?.userAgent) : false;
  }, [hasWindowLoaded]);

  // Memoize query ready state
  const isQueryReady = useMemo(() => {
    return !prefsQuery?.isPaused || !prefsQuery?.isLoading;
  }, [prefsQuery?.isPaused, prefsQuery?.isLoading]);

  // Memoize modal conditions to prevent unnecessary recalculations
  const shouldShowModalConditions = useMemo(() => {
    return (
      shoppingPrefEnable &&
      hasWindowLoaded &&
      hasHydrated &&
      isQueryReady &&
      !hasUserSelectedPrefs &&
      source !== 'session' &&
      pathname === '/' &&
      !isUserBot
    );
  }, [
    shoppingPrefEnable,
    hasWindowLoaded,
    hasHydrated,
    isQueryReady,
    hasUserSelectedPrefs,
    source,
    pathname,
    isUserBot,
  ]);

  // Setup CACT listeners (separated from modal logic for better performance)
  useEffect(() => {
    if (!cact || hasSeenCMP) {
      return;
    }

    logMessage(
      '[useShoppingPreferences.hook] Tag Manager (cact) found, setting up listeners for CMP',
      'info',
      pathname,
    );

    const handleConsentUpdate = () => {
      logMessage('[useShoppingPreferences.hook] Consent has been updated', 'info', pathname);
      setHasSeenCMP(true);
    };

    const handleConsentGet = (result) => {
      logMessage('[useShoppingPreferences.hook] Consent data received', 'info', pathname);
      if (result?.consent?.status !== 'unset') {
        logMessage('[useShoppingPreferences.hook] User has seen CMP and set', 'info', pathname);
        setHasSeenCMP(true);
      }
    };

    cact('consent.onUpdate', handleConsentUpdate);
    cact('consent.get', handleConsentGet);

    // TODO: Cleanup function would be needed if CACT supports removing listeners. Check if we can remove listener
  }, [cact, hasSeenCMP, pathname]);

  // Log when CACT is not found (separate effect to avoid unnecessary re-runs)
  useEffect(() => {
    if (hasWindowLoaded && !cact) {
      logMessage('[useShoppingPreferences.hook] Tag Manager (cact) not found', 'warn', pathname);
    }
  }, [cact, hasWindowLoaded, pathname]);

  // Handle modal visibility based on memoized conditions
  useEffect(() => {
    const shouldShow = shouldShowModalConditions && hasSeenCMP;
    setShowPrefsModal(shouldShow);
  }, [shouldShowModalConditions, hasSeenCMP]);

  // Track persisted (non-session) prefs
  useEffect(() => {
    if (prefs?.universe && source !== 'session') {
      setPersistedPreferences(prefs);
    }
  }, [prefs?.universe, source]);

  const setSessionPreferences = useCallback(
    (newPrefs: PreferencesWithoutTimestamp): void => {
      const updated: ShoppingPreferences = { ...newPrefs, last_updated: nowISO() };
      safeStorage.setSession(updated);
      setSource('session');
      // Set a cookie with new prefs so we can render on server
      setCookieShoppingPrefs(newPrefs, !!safeStorage?.getLocal());
      updateHomepageWithNewPrefs(newPrefs);
    },
    [prefs, persistedPreferences],
  );

  const updatePreferences = useCallback(
    async (newPrefs: PreferencesWithoutTimestamp): Promise<void> => {
      const updated: ShoppingPreferences = { ...newPrefs, last_updated: nowISO() };

      safeStorage.setLocal(updated);
      if (isAuthenticated) {
        try {
          await updatePrefsMutation.mutateAsync(updated);
        } catch (error) {
          logger.error('[useShoppingPreferences] Failed to sync preferences to backend:', error);
          // Proceed with local update so cookie and ME menu state stay in sync
        }
      }
      setSessionPreferences(updated);
      if (!isAuthenticated) {
        updateHomepageWithNewPrefs(updated);
      }
      setGlobalPrefrences(updated, 'local'); // Mark as user-selected
    },
    [isAuthenticated, updatePrefsMutation, setSessionPreferences],
  );

  const clearPreferences = useCallback((storage: StorageToClear): void => {
    if (storage === 'session' || storage === 'all') {
      safeStorage.clearSession();
    }

    if (storage === 'local' || storage === 'all') {
      safeStorage.clearLocal();
      // Reset to defaults instead of null
      const defaultPrefs = createDefaultPreferences();
      setGlobalPrefrences(defaultPrefs, 'local', false); // Reset user selection state
    }
  }, []);

  return {
    preferences: prefs,
    persistedPreferences,
    setPreferences: updatePreferences,
    setSessionPreferences,
    clearPreferences,
    source,
    isLoading: prefsQuery.isLoading,
    isError: prefsQuery.isError || !!prefsQuery.error,
    shoppingPrefEnable,
    setShowPrefsModal,
    showPrefsModal,
    hasUserSelectedPrefs,
  };
};
